import React from "react";
import { Edit3, Files, X, Edit } from "lucide-react";

const KnowledgeFileTabs = ({
  knowledgeItems,
  filterType,
  onDelete,
  onEdit,
  isDeletingKnowledge,
  isLoading,
  searchTerm,
  onSearchChange,
}) => {
  // Filter items based on type
  const filteredItems = knowledgeItems.filter((item) => {
    if (filterType === "manual") {
      return item.type === "manual" || item.type === "text";
    }
    if (filterType === "document") {
      return (
        item.type === "document" ||
        item.type === "file" ||
        item.type === "upload" ||
        item.type === "pdf" ||
        item.type === "docx" ||
        item.type === "txt" ||
        item.type === "doc"
      );
    }
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
          Loading...
        </span>
      </div>
    );
  }

  if (filteredItems.length === 0) {
    const emptyMessage =
      filterType === "manual"
        ? "No manual content added yet"
        : filterType === "document"
        ? "No documents uploaded yet"
        : "No knowledge items found";

    return (
      <div className="text-center py-6">
        <div className="text-gray-400 dark:text-gray-500 mb-2">
          {filterType === "manual" ? "📝" : "📁"}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {emptyMessage}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 min-h-[60px]">
      {/* Tab Bar */}
      <div className="flex items-center overflow-x-auto scrollbar-hide border-b border-gray-200 dark:border-gray-600">
        {filteredItems.map((item, index) => {
          const isManual = item.type === "manual";
          const Icon = isManual ? Edit3 : Files;
          const title = item.title || item.filename || `Item ${item.id}`;

          return (
            <div
              key={item.id}
              className={`
                group flex items-center space-x-2 px-3 py-2 border-r border-gray-200 dark:border-gray-600 
                bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700
                transition-colors min-w-0 max-w-[200px] cursor-default
                ${index === 0 ? "rounded-tl-lg" : ""}
              `}
            >
              {/* Icon */}
              <Icon
                className={`
                w-4 h-4 flex-shrink-0
                ${
                  isManual
                    ? "text-blue-500 dark:text-blue-400"
                    : "text-green-500 dark:text-green-400"
                }
              `}
              />

              {/* Title */}
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                {title}
              </span>

              {/* Type Badge */}
              <span
                className={`
                px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0
                ${
                  isManual
                    ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                    : "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                }
              `}
              >
                {isManual ? "M" : "D"}
              </span>

              {/* Actions */}
              <div className="flex items-center space-x-1 flex-shrink-0 opacity-0 group-hover:opacity-100">
                {/* Edit Button (only for manual type) */}
                {isManual && (
                  <button
                    onClick={() => onEdit && onEdit(item)}
                    disabled={isDeletingKnowledge}
                    className="w-4 h-4 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
                    title="Edit content"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                )}

                {/* Delete Button */}
                <button
                  onClick={() => onDelete(item)}
                  disabled={isDeletingKnowledge}
                  className="w-4 h-4 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                  title="Delete item"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Content Preview Area */}
      <div className="p-3">
        <div className="text-xs text-gray-600 dark:text-gray-400">
          <div className="flex items-center space-x-3">
            <span className="font-medium">{filteredItems.length} items</span>
            <span>•</span>
            <span>
              {filteredItems.reduce(
                (total, item) => total + (item.chunks_created || 0),
                0
              )}{" "}
              chunks
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeFileTabs;
